import React, { useEffect, useContext, useState, useCallback } from 'react';
import { connect } from 'react-redux';
import { translationFunctionSelector, PureTranslator } from 'ggApp/modules/translator';
import { OrderContext } from 'ggApp/shared/context/Order/OrderContext';
import { ButtonV2, RadioBoxGroup, RadioBox } from '@getgrover/ui';
import CloseSmall from 'ggApp/shared/icons/CloseSmall';
import StraightFaceIcon from 'ggApp/shared/icons/StraightFace';
import { HappyFaceIcon } from 'ggApp/shared/icons/HappyFace';
import { StarFace } from 'ggApp/shared/icons/StarFace';
import { SadFaceIcon } from 'ggApp/shared/icons/SadFaceIcon';
import { useMutation } from '@apollo/client';
import { ORDER_MOOD_REASON, CAST_MOOD } from '../../../../../queries/getVotingTicket';
import { MoodEnum } from './types';
import { CloseButton, Container, Title, Text, Textarea } from './styles';

type EnhancedProps = {
    translate: (key: string) => string;
};

const getTranslationKeys = (mood: MoodEnum) => {
    switch (mood) {
        case MoodEnum.angry:
        case MoodEnum.sad:
            return {
                icon: <SadFaceIcon size={16} color="#333" />,
                title: <PureTranslator tk="CHECKOUT_SURVEY_SAD_TITLE" />,
                text: <PureTranslator tk="CHECKOUT_SURVEY_SAD_TEXT" />,
            };
        case MoodEnum.neutral:
            return {
                icon: <StraightFaceIcon size={16} color="#333" />,
                title: <PureTranslator tk="CHECKOUT_SURVEY_NEUTRAL_TITLE" />,
                text: <PureTranslator tk="CHECKOUT_SURVEY_NEUTRAL_TEXT" />,
            };
        case MoodEnum.happy:
            return {
                icon: <HappyFaceIcon size={16} />,
                title: <PureTranslator tk="CHECKOUT_SURVEY_HAPPY_TITLE" />,
                text: <PureTranslator tk="CHECKOUT_SURVEY_HAPPY_TEXT" />,
            };
        default:
        case MoodEnum.inLove:
            return {
                icon: <StarFace size={16} />,
                title: <PureTranslator tk="CHECKOUT_SURVEY_INLOVE_TITLE" />,
                text: <PureTranslator tk="CHECKOUT_SURVEY_INLOVE_TEXT" />,
            };
    }
};

export const FeedbackSurveyComponent = ({
    translate,
    votingTicket,
    setSurveyError,
}: {
    translate: EnhancedProps;
    votingTicket: MoodEnum;
    setSurveyError: (val: boolean) => void;
}): JSX.Element => {
    const { orderNumber } = useContext(OrderContext);

    const [comment, setComment] = useState();
    const [hide, setHide] = useState(false);
    const [voteSlug, setVoteSlug] = useState();
    const [successScreen, setSuccessScreen] = useState<boolean>(false);

    const [fetchVotingOptions, { data }] = useMutation(ORDER_MOOD_REASON, {
        onError: () => setSurveyError(true),
    });

    const [callCastVoteMutation] = useMutation(CAST_MOOD, {
        onCompleted: () => setSuccessScreen(true),
    });

    useEffect(() => {
        fetchVotingOptions({
            variables: {
                entityId: orderNumber,
                slug: 'checkout-feedback',
            },
        });
    }, [orderNumber, fetchVotingOptions]);

    const onSlugChange = useCallback((e) => setVoteSlug(e.target.value), []);

    const { icon, text, title } = getTranslationKeys(votingTicket);

    const placeholderText = translate('CHECKOUT_SURVEY_TEXTAREA_PLACEHOLDER');

    return (
        <Container hide={hide}>
            <CloseButton onClick={() => setHide(true)} icon={<CloseSmall />} small />

            {successScreen ? (
                <>
                    <Title>
                        <PureTranslator tk="CHECKOUT_SURVEY_DONE_TITLE" />
                    </Title>
                    <Text>
                        <PureTranslator tk="CHECKOUT_SURVEY_DONE_TEXT" />
                    </Text>
                </>
            ) : (
                <>
                    <Title>
                        {title}
                        <span>{icon}</span>
                    </Title>
                    <Text>{text}</Text>

                    <RadioBoxGroup onChange={onSlugChange}>
                        {data?.getVotingTicket?.payload?.options?.map((i) => (
                            <RadioBox key={i.slug} value={i.slug} label={i.label} />
                        ))}
                    </RadioBoxGroup>

                    <Textarea
                        rows={2}
                        value={comment}
                        autoComplete="off"
                        name="feedback"
                        maxLength={10000}
                        onChange={(e) => setComment(e.target.value)}
                        placeholder={placeholderText}
                    />

                    <ButtonV2
                        small
                        onClick={() =>
                            callCastVoteMutation({
                                variables: {
                                    comment,
                                    voteSlug,
                                    ticket: data?.getVotingTicket?.ticket,
                                },
                            })
                        }
                        disabled={!voteSlug}
                    >
                        <PureTranslator tk="CHECKOUT_SURVEY_SUBMIT_CTA" />
                    </ButtonV2>
                </>
            )}
        </Container>
    );
};

const enhance = connect<EnhancedProps, {}, any>((state) => ({
    translate: translationFunctionSelector(state),
}));

export const FeedbackSurvey = enhance(FeedbackSurveyComponent);
